import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { Modal, Input, Image, Upload, Button, notification } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { FileUploadIcon, CirclePlusIcon } from 'components/Elements/CustomSVGIcon';
import AuthImage from 'components/Elements/AuthImage';
import { AppDispatch } from 'core/types';
import { IncidentImage } from 'core/incidents/types';
import { stripHtmlTags } from 'core/utils/helpers';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import { uploadIncidentImage, updateIncidentImage, deleteIncidentImage } from 'core/incidents/incidentsApi';
import './incidentImages.scss';

interface IncidentImagesProps {
    incidentId: number;
    images: IncidentImage[];
    isClosed: boolean;
}

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/webp';
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    if (parts.length <= 1) return '';
    const ext = parts[parts.length - 1];
    return ext ? ext.toLowerCase() : '';
};

const { Dragger } = Upload;

const IncidentImages: FC<IncidentImagesProps> = ({ incidentId, images, isClosed }) => {
    const dispatch = useDispatch<AppDispatch>();

    const [showAllImages, setShowAllImages] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadDescription, setUploadDescription] = useState('');
    const [editingImage, setEditingImage] = useState<IncidentImage | null>(null);
    const [editDescription, setEditDescription] = useState('');
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deletingImage, setDeletingImage] = useState<IncidentImage | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleUpload = async () => {
        if (!selectedFile) return;
        setIsUploading(true);
        try {
            const result = await dispatch(uploadIncidentImage({
                incidentId,
                file: selectedFile,
                ...(uploadDescription ? { description: uploadDescription } : {}),
            }));
            if (uploadIncidentImage.fulfilled.match(result)) {
                setUploadModalVisible(false);
                setSelectedFile(null);
                setUploadDescription('');
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleEdit = (image: IncidentImage) => {
        setEditingImage(image);
        setEditDescription(image.description || '');
        setEditModalVisible(true);
    };

    const handleUpdate = async () => {
        if (!editingImage) return;
        setIsUpdating(true);
        try {
            const result = await dispatch(updateIncidentImage({
                incidentId,
                imageId: editingImage.id,
                description: editDescription || null,
            }));
            if (updateIncidentImage.fulfilled.match(result)) {
                setEditModalVisible(false);
                setEditingImage(null);
                setEditDescription('');
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = (image: IncidentImage) => {
        setDeletingImage(image);
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingImage) return;
        setIsDeleting(true);
        try {
            const result = await dispatch(deleteIncidentImage({ incidentId, imageId: deletingImage.id }));
            if (deleteIncidentImage.fulfilled.match(result)) {
                setDeleteModalVisible(false);
                setDeletingImage(null);
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const handleBeforeUpload = (file: File) => {
        const ext = getFileExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            setSelectedFile(null);
            notification.error({
                message: 'Unsupported File Type',
                description: `The file "${file.name}" has an unsupported extension. Allowed: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}.`,
            });
            return Upload.LIST_IGNORE;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setSelectedFile(null);
            notification.error({
                message: 'File Too Large',
                description: `The file "${file.name}" exceeds the maximum size of 10MB.`,
            });
            return Upload.LIST_IGNORE;
        }

        setSelectedFile(file);
        return false;
    };

    const visibleImages = (images || []).filter((image) => image?.id);
    const IMAGE_LIMIT = 5;
    const hasMoreImages = visibleImages.length > IMAGE_LIMIT;
    const imagesToRender = showAllImages ? visibleImages : visibleImages.slice(0, IMAGE_LIMIT);

    return (
        <div className="incident-images-section">
            <div className="incident-images-header-row">
                <div className="incident-images-header">
                    <h4>Incident Images</h4>
                    {!isClosed && (
                        <button
                            type="button"
                            className="add-image-link"
                            onClick={() => setUploadModalVisible(true)}>
                            <CirclePlusIcon />
                            Upload Image
                        </button>
                    )}
                </div>
                <div className="incident-images-header-spacer" />
            </div>

            {visibleImages.length > 0 ? (
                <Image.PreviewGroup>
                    <div className="incident-images-grid">
                        {imagesToRender.map((image) => (
                            <div className="incident-image-card" key={image.id}>
                                <div className="image-wrapper">
                                    <AuthImage
                                        src={image.url}
                                        alt={image.description || image.filename}
                                        width="100%"
                                        height="100%"
                                        style={{ objectFit: 'cover' }} />
                                </div>
                                <div className={`image-info${isClosed ? ' image-info--no-actions' : ''}`}>
                                    <p className="image-description">
                                        {image.description ? stripHtmlTags(image.description) : '\u00A0'}
                                    </p>
                                    <p className="image-meta">
                                        {image.uploadedBy && <span>{image.uploadedBy}</span>}
                                        {image.uploadedBy && image.uploadDate && <span> - </span>}
                                        {image.uploadDate && <span>{dayjs(image.uploadDate).format('DD/MM/YYYY')}</span>}
                                    </p>
                                    {!isClosed && (
                                        <div className="image-actions">
                                            <button
                                                type="button"
                                                className="image-action-btn"
                                                onClick={() => handleEdit(image)}>
                                                <EditOutlined />
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="image-action-btn image-action-btn--delete"
                                                onClick={() => handleDelete(image)}>
                                                <DeleteOutlined />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {hasMoreImages && (
                        <button
                            type="button"
                            className="view-all-images-link"
                            onClick={() => setShowAllImages((prev) => !prev)}>
                            {showAllImages ? 'Show Less' : `View All (${visibleImages.length})`}
                        </button>
                    )}
                </Image.PreviewGroup>
            ) : (
                <div className="no-images-message">No images uploaded yet.</div>
            )}

            <Modal
                title="Upload Image"
                open={uploadModalVisible}
                centered
                footer={null}
                width={650}
                wrapClassName="site-image-modal"
                onCancel={() => {
                    setUploadModalVisible(false);
                    setSelectedFile(null);
                    setUploadDescription('');
                }}>
                <Dragger
                    accept={ACCEPTED_IMAGE_TYPES}
                    beforeUpload={handleBeforeUpload}
                    onRemove={() => setSelectedFile(null)}
                    showUploadList={false}
                    className="file-dragger-container">
                    <div className="file-uploader-content">
                        <div className="icon-wrapper">
                            <FileUploadIcon />
                        </div>
                        <p className="caption">
                            {selectedFile ? selectedFile.name : 'Drag and drop an image here, or click to upload'}
                        </p>
                        <p className="sub-caption">
                            Supports JPG, PNG, GIF, WebP (max 10MB)
                        </p>
                    </div>
                </Dragger>
                <Input.TextArea
                    placeholder="Description (optional)"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    className="notes-textarea"
                    rows={3}
                    maxLength={MAX_LONG_TEXT_LENGTH} />
                <div className="modal-buttons">
                    <Button
                        type="primary"
                        className="save-button"
                        disabled={!selectedFile}
                        loading={isUploading}
                        onClick={handleUpload}>
                        Upload
                    </Button>
                </div>
            </Modal>

            <Modal
                title="Edit Description"
                open={editModalVisible}
                centered
                footer={null}
                width={650}
                wrapClassName="site-image-modal"
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingImage(null);
                    setEditDescription('');
                }}>
                <Input.TextArea
                    placeholder="Description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="notes-textarea"
                    rows={3}
                    maxLength={MAX_LONG_TEXT_LENGTH} />
                <div className="modal-buttons">
                    <Button
                        type="primary"
                        className="save-button"
                        loading={isUpdating}
                        onClick={handleUpdate}>
                        Save
                    </Button>
                </div>
            </Modal>

            <Modal
                title="Delete Image"
                open={deleteModalVisible}
                centered
                footer={null}
                width={650}
                wrapClassName="site-image-modal"
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setDeletingImage(null);
                }}>
                <p className="delete-confirm-text">Are you sure you want to delete this image?</p>
                <div className="button-wrapper">
                    <button
                        type="button"
                        className="apply-button"
                        disabled={isDeleting}
                        onClick={handleConfirmDelete}>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={() => {
                            setDeleteModalVisible(false);
                            setDeletingImage(null);
                        }}>
                        Cancel
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default IncidentImages;
